import { useAuth } from "@clerk/clerk-react";
import { P, match } from "ts-pattern";
import { AppLayout } from "./components/AppLayout";
import { Redirect } from "./components/Redirect";
import { clearOutdatedPersistedData } from "./hooks/usePersistedState";
import { LoadingPage } from "./pages/LoadingPage";
import { MyProfilePage } from "./pages/MyProfilePage";
import { SigninPage } from "./pages/SigninPage";
import { Signout } from "./pages/Signout";
import { parseApplicationKey } from "./utils/applications";
import { router } from "./utils/routes";

// Clear outdated data persisted on localStorage
clearOutdatedPersistedData();

export const App: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const route = router.useRoute(["SigninArea", "Signout"]);

  if (!isLoaded) {
    return <LoadingPage />;
  }

  return match({ isSignedIn, route })
    .with(
      { route: { name: "SigninArea" } },
      ({
        route: {
          params: { from_app, redirect_to },
        },
      }) => <SigninPage fromApp={parseApplicationKey(from_app)} redirectTo={redirect_to} />,
    )
    .with({ isSignedIn: false }, () => {
      const url = window.location.pathname + window.location.search;
      const redirectTo = url === router.Signout() || url === "/" ? undefined : url;
      return <Redirect to={router.SigninIndex({ redirect_to: redirectTo })} />;
    })
    .with({ route: { name: "Signout" } }, () => <Signout />)
    .otherwise(() => <SignedInApp />);
};

const SignedInApp: React.FC = () => {
  const route = router.useRoute(["MyProfile"]);

  const fromApp = parseApplicationKey(route?.params.from_app);

  return (
    <AppLayout fromApp={fromApp}>
      {match(route)
        .with({ name: "MyProfile" }, () => <MyProfilePage fromApp={fromApp} />)
        .with(P.nullish, () => (
          <Redirect replace={true} to={router.MyProfile({ from_app: fromApp })} />
        ))
        .exhaustive()}
    </AppLayout>
  );
};
